// import 'core-js';
// import 'core-js/es/regexp';
import React, { useCallback } from 'react';
import { Switch, Route, MemoryRouter } from 'react-router-dom';
import { IntroPage } from '../pages/IntroPage';
import * as Sentry from '@sentry/browser';
import { ReservedContentArgs } from '../../types';
import { AuthContext } from '@food/auth/build/dist/components/AuthContext';
import { AuthRoutes } from '@food/auth/build/dist/components/AuthRoutes';
import { UserData } from '@food/auth/build/dist/components/UserData';
import { Redirect } from 'react-router';
import { cache, httpLink } from '../../utils/client';
import { ApolloProvider } from 'react-apollo';
import { WithTranslation, withTranslation } from 'react-i18next';
import { t } from '../../utils/labels';
import { authConfigDefault } from '../../utils/config';
import { RedirectHandler } from '@food/auth/build/dist/types';
import { history } from '../../utils/history';
import { auth } from '@food/auth/build/dist/logic/auth';
import { Channel } from '@food/sync';
import { stacked } from '../../utils/broadcastChannel';
import { ApolloLink } from 'apollo-link';
import { targetSiteLink } from '@food/auth/build/dist/links/targetSiteLink';
import { RenewLink } from '@food/auth/build/dist/links/RenewLink';
import ApolloClient from 'apollo-client';
import { checkForAchievements } from '../../utils/misc';
import { AnalyticsListener } from '../AnalyticsListener';

interface IProps extends WithTranslation {
	args: ReservedContentArgs;
}

const authConfig = {
	...authConfigDefault,
};

const onRedirect: RedirectHandler = (user, redirectUrl) => {
	if (!user) {
		history.push('/');
	} else if (history.location.pathname === '/login') {
		window.history.replaceState(null, '', redirectUrl);
	}
};

const authManager = auth(authConfig);

const authChannel = new Channel('@food/auth');

const userChangedHandler: (
	oldUser: {
		id: string;
		name: string;
		email: string;
	} | null,
	newUser: {
		id: string;
		name: string;
		email: string;
	} | null,
) => Promise<void> = async (
	_,
	newUser: {
		id: string;
		name: string;
		email: string;
	} | null,
) => {
		Sentry.configureScope((scope) => {
			if (newUser) {
				scope.setUser({
					id: newUser.id,
					username: newUser.name,
					email: newUser.email,
				});
			} else {
				scope.setUser(null);
			}
		});

		await client.cache.reset();
		client.resetStore();

		onRedirect(newUser, '/');
	};

authChannel.onmessage = stacked((events: any[]) => {
	const lastEvent = events.filter((e) => e.data.type === 'USER_CHANGED')[0];

	if (lastEvent) {
		const { oldUser, newUser } = lastEvent.data.args;
		userChangedHandler(oldUser, newUser);
	}
}, 100);

const link = ApolloLink.from([
	new (targetSiteLink(authConfig))(),
	new (RenewLink(authManager))(),
	httpLink,
]);

export const client = new ApolloClient({
	link,
	cache,
});

const ReservedContent: React.FC<IProps> = ({ args: { labels, authRules, hiddenNodes } }) => {
	checkForAchievements();

	const showHiddenContent = useCallback(() => {
		hiddenNodes.forEach((className) => {
			const elements = document.querySelectorAll('.' + className);
			elements.forEach((node) => {
				node.classList.remove(className);
			});
		});
	}, [hiddenNodes]);

	if (authRules.length === 0) {
		Sentry.withScope(() => {
			Sentry.captureException(
				new Error(
					'Chiamata applicazione per contenuto riservato senza alcuna condizione',
				),
			);
		});
		showHiddenContent();
	}

	return (
		<AuthContext>
			<ApolloProvider client={client}>
				<MemoryRouter initialEntries={['/']}>
					<AnalyticsListener />
					<AuthRoutes
						config={authConfig}
						authManager={authManager}
						t={t}
						onRedirect={onRedirect}
					>
						<UserData>
							{({ userData }) => (
								<Switch>
									<Route
										path={'/'}
										exact
										component={() => (
											<IntroPage
												labels={labels}
												conditions={authRules}
												onCanView={() => showHiddenContent()}
												user={userData}
											/>
										)}
									/>
									<Redirect to={'/'} />
								</Switch>
							)}
						</UserData>
					</AuthRoutes>
				</MemoryRouter>
			</ApolloProvider>
		</AuthContext>
	);
};

export default withTranslation('translations')(ReservedContent);
